<template>
  <div class="dev-apply">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>开发者认证</h2>
      </div>

      <!-- 资质状态横幅 -->
      <div class="status-banner" :class="statusClass">
        <div class="status-main">
          <i :class="statusIcon"></i>
          <div>
            <p class="status-title">{{ statusTitle }}</p>
            <p class="status-desc">{{ statusDesc }}</p>
          </div>
        </div>
        <div v-if="isRejected" class="reject-reason">
          <b>驳回理由：</b>{{ info.auditRemark }}
        </div>
      </div>

      <div class="alert alert-info-light">
        通过实名认证成为开发者后，你可以上传并出售自己开发的插件，插件被购买后按平台规则扣除手续费，收益进入开发者账户，可申请提现。
        平台收取的手续费比例、收益冻结天数、提现规则以平台公告为准。
      </div>

      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="120px"
        class="apply-form"
        :disabled="formDisabled">
        <h3>开发者信息</h3>
        <el-form-item label="开发者展示名" prop="devName">
          <el-input v-model="form.devName" placeholder="将展示在插件详情页，建议使用品牌名或昵称" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="联系方式" prop="contact">
          <el-input v-model="form.contact" placeholder="手机号 / 微信号 / 邮箱，便于平台联系" maxlength="50" />
        </el-form-item>
        <el-form-item label="开发经验简介" prop="intro">
          <el-input
            type="textarea"
            :rows="4"
            v-model="form.intro"
            placeholder="介绍你的开发经验与代表作品，将作为平台审核参考"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <h3>实名认证</h3>
        <el-form-item label="证件类型" prop="certType">
          <el-radio-group v-model="form.certType">
            <el-radio :label="1">个人身份证</el-radio>
            <el-radio :label="2">企业对公</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="isCompany ? '企业名称' : '真实姓名'" prop="realName">
          <el-input v-model="form.realName" :placeholder="isCompany ? '请输入企业全称' : '请输入身份证上的姓名'" />
        </el-form-item>
        <el-form-item :label="isCompany ? '统一社会信用代码' : '身份证号'" prop="certNo">
          <el-input v-model="form.certNo" :placeholder="isCompany ? '请输入统一社会信用代码' : '请输入 18 位身份证号'" />
        </el-form-item>

        <el-form-item :label="isCompany ? '营业执照' : '身份证正面'" prop="certFront">
          <div class="upload-box">
            <el-upload
              :headers="headers"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="(res, file) => handleCertSuccess(res, file, 'certFront')"
              :before-upload="beforeImageUpload">
              <img v-if="form.certFront" :src="form.certFront" class="cert-img">
              <i v-else class="el-icon-plus cert-uploader-icon"></i>
            </el-upload>
            <p class="upload-tip">{{ isCompany ? '请上传营业执照原件照片' : '请确保头像面信息清晰可见' }}</p>
          </div>
        </el-form-item>

        <el-form-item v-if="!isCompany" label="身份证反面" prop="certBack">
          <div class="upload-box">
            <el-upload
              :headers="headers"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="(res, file) => handleCertSuccess(res, file, 'certBack')"
              :before-upload="beforeImageUpload">
              <img v-if="form.certBack" :src="form.certBack" class="cert-img">
              <i v-else class="el-icon-plus cert-uploader-icon"></i>
            </el-upload>
            <p class="upload-tip">请上传国徽面照片</p>
          </div>
        </el-form-item>

        <el-form-item label="其他材料">
          <div class="upload-box">
            <el-upload
              :headers="headers"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="(res, file) => handleCertSuccess(res, file, 'certOther')"
              :before-upload="beforeImageUpload">
              <img v-if="form.certOther" :src="form.certOther" class="cert-img">
              <i v-else class="el-icon-plus cert-uploader-icon"></i>
            </el-upload>
            <p class="upload-tip">可选，如资质证书、作品链接截图等</p>
          </div>
        </el-form-item>

        <el-form-item v-if="!formDisabled">
          <el-checkbox v-model="agreed">我已阅读并同意《开发者入驻协议》与《插件上架规范》</el-checkbox>
        </el-form-item>

        <el-form-item v-if="!formDisabled">
          <el-button type="primary" :loading="submitting" @click="submit">提交审核</el-button>
          <el-button @click="resetForm">重 置</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { getDeveloperInfo, applyDeveloper } from '@/api/developer'
import { getToken } from '@/utils/auth'

const emptyForm = () => ({
  devName: '',
  contact: '',
  intro: '',
  certType: 1,
  realName: '',
  certNo: '',
  certFront: '',
  certBack: '',
  certOther: ''
})

export default {
  name: 'devApply',
  data () {
    return {
      headers: { Authorization: 'Bearer ' + getToken() },
      info: {},
      form: emptyForm(),
      agreed: false,
      submitting: false,
      rules: {
        devName: [{ required: true, message: '开发者展示名不能为空', trigger: 'blur' }],
        contact: [{ required: true, message: '联系方式不能为空', trigger: 'blur' }],
        intro: [{ required: true, message: '开发经验简介不能为空', trigger: 'blur' }],
        realName: [{ required: true, message: '姓名或企业名称不能为空', trigger: 'blur' }],
        certNo: [{ required: true, message: '证件号码不能为空', trigger: 'blur' }],
        certFront: [{ required: true, message: '请上传证件照片', trigger: 'change' }]
      }
    }
  },
  computed: {
    uploadAction () {
      return this.BaseUrl + '/codeStoreFrontAdmin/developerUploadCert'
    },
    isCompany () {
      return this.form.certType === 2
    },
    hasApply () {
      return !!this.info.id
    },
    isPassed () {
      return Number(this.info.status) === 1
    },
    isRejected () {
      return Number(this.info.status) === 2
    },
    formDisabled () {
      // 审核中或已通过时表单只读，避免重复提交
      return this.hasApply && !this.isRejected
    },
    statusClass () {
      if (!this.hasApply) return 'status-none'
      if (this.isPassed) return 'status-pass'
      if (this.isRejected) return 'status-reject'
      return 'status-pending'
    },
    statusIcon () {
      if (!this.hasApply) return 'el-icon-info'
      if (this.isPassed) return 'el-icon-circle-check'
      if (this.isRejected) return 'el-icon-circle-close'
      return 'el-icon-time'
    },
    statusTitle () {
      if (!this.hasApply) return '尚未提交开发者申请'
      if (this.isPassed) return '开发者认证已通过'
      if (this.isRejected) return '开发者认证未通过'
      return '开发者申请审核中'
    },
    statusDesc () {
      if (!this.hasApply) return '完成实名认证后即可上传并出售自己开发的插件'
      if (this.isPassed) return '你可以上传插件、查看插件订单收益并申请提现'
      if (this.isRejected) return '请根据驳回理由修改资料后重新提交'
      return '平台将在 1-3 个工作日内完成审核，请耐心等待'
    }
  },
  methods: {
    loadInfo () {
      getDeveloperInfo().then(res => {
        const { code, data, msg } = res
        if (code !== 0) {
          this.$message.error(msg || '获取开发者信息失败')
          return
        }
        this.info = data.info || {}
        if (this.info.id) {
          this.form = {
            devName: this.info.devName || '',
            contact: this.info.contact || '',
            intro: this.info.intro || '',
            certType: Number(this.info.certType) || 1,
            realName: this.info.realName || '',
            // 证件号后端已脱敏，重新提交时必须重新填写
            certNo: '',
            certFront: this.info.certFront || '',
            certBack: this.info.certBack || '',
            certOther: this.info.certOther || ''
          }
        }
      })
    },
    beforeImageUpload (file) {
      const isImage = file.type.indexOf('image') === 0
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isImage) {
        this.$message.error('只能上传图片文件')
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
      }
      return isImage && isLt5M
    },
    handleCertSuccess (res, file, field) {
      const { code, data, msg } = res
      if (code === 0 && data) {
        this.form[field] = data.fileUrl
        this.$refs.form && this.$refs.form.validateField(field)
      } else {
        this.$message.error(msg || '上传失败')
      }
    },
    submit () {
      if (!this.agreed) {
        this.$message.warning('请先阅读并同意相关协议')
        return
      }
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        applyDeveloper(this.form).then(res => {
          const { code, msg } = res
          if (code === 0) {
            this.$message.success(msg || '申请已提交')
            this.agreed = false
            this.loadInfo()
          } else {
            this.$message.error(msg || '提交失败')
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    resetForm () {
      this.form = emptyForm()
      this.$refs.form && this.$refs.form.clearValidate()
    }
  },
  created () {
    this.loadInfo()
  }
}
</script>

<style lang="less" scoped>
.dev-apply {
  .panel-heading h2 {
    margin: 0 0 20px 0;
    font-size: 22px;
    color: #303133;
  }

  .status-banner {
    display: flex;
    flex-direction: column;
    padding: 18px 20px;
    border-radius: 6px;
    margin-bottom: 18px;

    .status-main {
      display: flex;
      align-items: center;

      i {
        font-size: 30px;
        margin-right: 14px;
      }

      .status-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
      }

      .status-desc {
        margin: 4px 0 0 0;
        font-size: 13px;
        opacity: 0.85;
      }
    }

    .reject-reason {
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px dashed rgba(0, 0, 0, 0.15);
      font-size: 13px;
    }
  }

  .status-none {
    background: #f4f4f5;
    color: #606266;
  }

  .status-pending {
    background: #fdf6ec;
    color: #e6a23c;
  }

  .status-pass {
    background: #f0f9eb;
    color: #67c23a;
  }

  .status-reject {
    background: #fef0f0;
    color: #f56c6c;
  }

  .alert-info-light {
    background-color: #d9edf7;
    border-color: #c6e4f3;
    color: #3a87ad;
  }

  .alert {
    padding: 15px;
    margin-bottom: 17px;
    border: 1px solid transparent;
    border-radius: 3px;
    font-size: 13px;
    line-height: 22px;
  }

  .apply-form {
    max-width: 720px;

    h3 {
      margin: 26px 0 16px 0;
      font-size: 16px;
      color: #444;
      border-left: 3px solid #0d95e8;
      padding-left: 8px;
    }
  }

  .upload-box {
    display: inline-block;

    .cert-img {
      width: 150px;
      height: 100px;
      display: block;
      object-fit: cover;
      border-radius: 4px;
    }

    .cert-uploader-icon {
      font-size: 26px;
      color: #8c939d;
      width: 150px;
      height: 100px;
      line-height: 100px;
      text-align: center;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        border-color: #0d95e8;
        color: #0d95e8;
      }
    }

    .upload-tip {
      margin: 6px 0 0 0;
      font-size: 12px;
      color: #909399;
    }
  }
}
</style>
